<template>
  <Modal
    title="Your session will expire soon"
    :show="showModal"
    icon-type="clock"
    :show-close-btn="false"
  >
    <p class="mt-4 text-center text-fp-gray-5 font-medium">
      You will be logged out in
      <span class="font-bold text-fp-gray-6 mono-font">{{
        data.secondsRemaining < 0 ? 0 : data.secondsRemaining
      }}</span>
      seconds unless you extend your session
    </p>
    <div class="flex mt-6 gap-3 md:gap-5 flex-col">
      <div class="flex justify-center">
        <LoadingButton
          type="button"
          class="btn-secondary w-full"
          :args="{ showSpinner: data.isLoading }"
          :disabled="sessionExpired"
          @click="extendSession"
        >
          {{ buttonText }}
        </LoadingButton>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import LoadingButton from '~/../member-webapp-shared/components/common/LoadingButton.vue'
import Modal from '~/components/common/Modal.vue'

const data = ref({
  isLoading: false,
  secondsRemaining: 9999999999,
})

const props = defineProps<{
  extendSession: () => Promise<unknown>
  expiryTime: Date | undefined
  authStatus: ComputedRef<'authenticated' | 'unauthenticated' | 'loading'>
  signOut: ({ external }: { external: boolean }) => Promise<unknown>
}>()

const sessionExpired = computed(() => data.value.secondsRemaining <= 0)
const showModal = computed(
  () =>
    props.authStatus.value === 'authenticated' &&
    data.value.secondsRemaining < 180
)
const buttonText = computed(() =>
  sessionExpired.value ? 'Your session has now expired' : 'Stay logged in'
)

const extendSession = async () => {
  data.value.isLoading = true
  await props.extendSession()
  data.value.isLoading = false
}

const getSecondsRemaining = (): number => {
  const expiryTime = props.expiryTime
  const millisecondsToExpiry = expiryTime
    ? expiryTime.getTime() - new Date().getTime()
    : 99999999999

  return Math.round(millisecondsToExpiry / 1000)
}

const sessionAboutToExpireCheck = (): void => {
  const startTimer = async (): Promise<void> => {
    const secondsToExpiry = getSecondsRemaining()
    data.value.secondsRemaining = secondsToExpiry

    const timeout = 500
    setTimeout(() => {
      startTimer()
    }, timeout)

    // wait until a few seconds after expiry to actually kick them out as it can
    // take a couple of seconds to extend their session if they press it just as the
    // counter hits 0.
    if (secondsToExpiry <= -5 && props.authStatus.value === 'authenticated') {
      await props.signOut({ external: true })
    }
  }

  startTimer()
}

onMounted(() => {
  sessionAboutToExpireCheck()
})
</script>
